import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router";

import type {
  JobQuestionAnswerDto,
  SaveJobAnswerItem,
} from "Infrastructure/Api/Api";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import type { ModuleQuestionDto } from "Modules/Api/Queries/useModuleQuestionsQuery";
import { ModuleQuestionType } from "Modules/Api/Queries/useModuleQuestionsQuery";
import { useSaveJobAnswersMutation } from "Projects/Api/Mutations/useSaveJobAnswersMutation";

const OTHER_VALUE = "__OTHER__";

interface AnswerState {
  [questionID: string]: {
    textAnswer?: string;
    selectedOptionIDs?: string[];
    otherText?: string;
  };
}

export interface JobQuestionnaireFormHandle {
  /** Validate required fields. Returns true if valid. */
  validate: () => boolean;
  /** Save answers if dirty. Returns true on success (or if not dirty). */
  saveIfDirty: () => Promise<boolean>;
  /** Validate + force save (even if not dirty). Returns true on success. */
  validateAndSave: () => Promise<boolean>;
}

interface JobQuestionnaireFormProps {
  questions: ModuleQuestionDto[];
  existingAnswers?: JobQuestionAnswerDto[] | null;
}

const isWithOtherType = (type: ModuleQuestionType): boolean =>
  type === ModuleQuestionType.SingleChoiceWithOther ||
  type === ModuleQuestionType.MultiChoiceWithOther;

const isSingleChoiceType = (type: ModuleQuestionType): boolean =>
  type === ModuleQuestionType.SingleChoice ||
  type === ModuleQuestionType.SingleChoiceWithOther;

const isMultiChoiceType = (type: ModuleQuestionType): boolean =>
  type === ModuleQuestionType.MultiChoice ||
  type === ModuleQuestionType.MultiChoiceWithOther;

export const JobQuestionnaireForm = forwardRef<
  JobQuestionnaireFormHandle,
  JobQuestionnaireFormProps
>(({ questions, existingAnswers }, ref): React.ReactNode => {
  const { t } = useResource();
  const { projectId, jobId } = useParams<{
    projectId: string;
    jobId: string;
  }>();

  const mutation = useSaveJobAnswersMutation(projectId!, jobId!);
  const isDirtyRef = useRef(false);

  const [answers, setAnswers] = useState<AnswerState>(() => {
    if (!existingAnswers || questions.length === 0) return {};
    const state: AnswerState = {};

    for (const a of existingAnswers) {
      const question = questions.find(
        (q) => q.moduleQuestionID === a.moduleQuestionID,
      );
      if (!question) continue;

      const selectedOptionIDs: string[] = [];
      for (const optionText of a.selectedOptions) {
        const option = question.options.find((o) => o.text === optionText);
        if (option) {
          selectedOptionIDs.push(option.moduleQuestionOptionID);
        }
      }

      const hasOther = isWithOtherType(question.type) && !!a.textAnswer?.trim();
      if (hasOther) {
        selectedOptionIDs.push(OTHER_VALUE);
      }

      state[question.moduleQuestionID] = {
        textAnswer:
          question.type !== ModuleQuestionType.Text
            ? undefined
            : (a.textAnswer ?? undefined),
        selectedOptionIDs,
        otherText: hasOther ? (a.textAnswer ?? undefined) : undefined,
      };
    }
    return state;
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const getAnswer = (questionID: string) =>
    answers[questionID] ?? {
      textAnswer: undefined,
      selectedOptionIDs: [],
      otherText: undefined,
    };

  const updateAnswer = (
    questionID: string,
    update: Partial<AnswerState[string]>,
  ) => {
    isDirtyRef.current = true;
    setAnswers((prev) => ({
      ...prev,
      [questionID]: { ...getAnswer(questionID), ...update },
    }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[questionID];
      return next;
    });
  };

  const validate = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};
    for (const q of questions) {
      if (!q.isRequired) continue;
      const answer = answers[q.moduleQuestionID] ?? {
        textAnswer: undefined,
        selectedOptionIDs: [],
        otherText: undefined,
      };
      const hasText = !!answer.textAnswer?.trim();
      const hasOptions = (answer.selectedOptionIDs?.length ?? 0) > 0;
      if (!hasText && !hasOptions) {
        newErrors[q.moduleQuestionID] = t(Resources.Validation.Required);
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [answers, questions, t]);

  const buildAnswerItems = useCallback((): SaveJobAnswerItem[] => {
    return questions
      .map((q) => {
        const answer = answers[q.moduleQuestionID] ?? {
          textAnswer: undefined,
          selectedOptionIDs: [],
          otherText: undefined,
        };
        const selectedIDs = (answer.selectedOptionIDs ?? []).filter(
          (id) => id !== OTHER_VALUE,
        );
        let textAnswer = answer.textAnswer ?? null;
        if (answer.otherText?.trim()) {
          textAnswer = answer.otherText;
        }
        return {
          moduleQuestionID: q.moduleQuestionID,
          textAnswer,
          selectedOptionIDs: selectedIDs,
        };
      })
      .filter(
        (a) =>
          a.textAnswer ||
          (a.selectedOptionIDs && a.selectedOptionIDs.length > 0),
      );
  }, [answers, questions]);

  const saveIfDirty = useCallback(async (): Promise<boolean> => {
    if (!isDirtyRef.current) return true;
    if (!validate()) return false;

    try {
      await mutation.mutateAsync({ answers: buildAnswerItems() });
      isDirtyRef.current = false;
      return true;
    } catch {
      return false;
    }
  }, [validate, buildAnswerItems, mutation]);

  const validateAndSave = useCallback(async (): Promise<boolean> => {
    if (!validate()) return false;

    try {
      await mutation.mutateAsync({ answers: buildAnswerItems() });
      isDirtyRef.current = false;
      return true;
    } catch {
      return false;
    }
  }, [validate, buildAnswerItems, mutation]);

  useImperativeHandle(ref, () => ({ validate, saveIfDirty, validateAndSave }), [
    validate,
    saveIfDirty,
    validateAndSave,
  ]);

  const renderSingleChoice = (question: ModuleQuestionDto) => {
    const answer = getAnswer(question.moduleQuestionID);
    const withOther = isWithOtherType(question.type);

    return (
      <FormControl error={!!errors[question.moduleQuestionID]} fullWidth>
        <RadioGroup
          value={answer.selectedOptionIDs?.[0] ?? ""}
          onChange={(e) =>
            updateAnswer(question.moduleQuestionID, {
              selectedOptionIDs: [e.target.value],
              otherText:
                e.target.value === OTHER_VALUE ? answer.otherText : undefined,
            })
          }
          sx={{ gap: 1 }}
        >
          {question.options.map((option) => (
            <FormControlLabel
              key={option.moduleQuestionOptionID}
              value={option.moduleQuestionOptionID}
              control={<Radio size="small" />}
              label={option.text}
            />
          ))}
          {withOther && (
            <FormControlLabel
              value={OTHER_VALUE}
              control={<Radio size="small" />}
              label={t(Resources.JobDetail.Questionnaire.OtherOption)}
            />
          )}
        </RadioGroup>
        {withOther && answer.selectedOptionIDs?.[0] === OTHER_VALUE && (
          <TextField
            fullWidth
            size="small"
            sx={{ mt: 1, ml: 0.5 }}
            value={answer.otherText ?? ""}
            onChange={(e) =>
              updateAnswer(question.moduleQuestionID, {
                otherText: e.target.value,
              })
            }
          />
        )}
      </FormControl>
    );
  };

  const renderMultiChoice = (question: ModuleQuestionDto) => {
    const answer = getAnswer(question.moduleQuestionID);
    const selected = answer.selectedOptionIDs ?? [];
    const withOther = isWithOtherType(question.type);

    return (
      <FormControl error={!!errors[question.moduleQuestionID]} fullWidth>
        <FormGroup sx={{ gap: 0.5 }}>
          {question.options.map((option) => (
            <FormControlLabel
              key={option.moduleQuestionOptionID}
              control={
                <Checkbox
                  size="small"
                  checked={selected.includes(option.moduleQuestionOptionID)}
                  onChange={(e) => {
                    const newSelected = e.target.checked
                      ? [...selected, option.moduleQuestionOptionID]
                      : selected.filter(
                          (id) => id !== option.moduleQuestionOptionID,
                        );
                    updateAnswer(question.moduleQuestionID, {
                      selectedOptionIDs: newSelected,
                    });
                  }}
                />
              }
              label={option.text}
            />
          ))}
          {withOther && (
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={selected.includes(OTHER_VALUE)}
                  onChange={(e) => {
                    const newSelected = e.target.checked
                      ? [...selected, OTHER_VALUE]
                      : selected.filter((id) => id !== OTHER_VALUE);
                    updateAnswer(question.moduleQuestionID, {
                      selectedOptionIDs: newSelected,
                      otherText: e.target.checked
                        ? answer.otherText
                        : undefined,
                    });
                  }}
                />
              }
              label={t(Resources.JobDetail.Questionnaire.OtherOption)}
            />
          )}
        </FormGroup>
        {withOther && selected.includes(OTHER_VALUE) && (
          <TextField
            fullWidth
            size="small"
            sx={{ mt: 1, ml: 0.5 }}
            value={answer.otherText ?? ""}
            onChange={(e) =>
              updateAnswer(question.moduleQuestionID, {
                otherText: e.target.value,
              })
            }
          />
        )}
      </FormControl>
    );
  };

  return (
    <Stack spacing={Spacing.S}>
      <Box>
        <Typography variant="body1" fontWeight={600}>
          {t(Resources.JobDetail.Questionnaire.Title)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t(Resources.JobDetail.Questionnaire.Description)}
        </Typography>
      </Box>

      {questions.map((question) => (
        <Box
          key={question.moduleQuestionID}
          sx={(theme) => ({
            border: `1px solid ${errors[question.moduleQuestionID] ? theme.palette.error.main : theme.colors.border}`,
            borderRadius: Radius.M,
            p: Spacing.S,
            "& .MuiFormControlLabel-label": {
              fontSize: 12,
              ml: 0.5,
            },
          })}
        >
          <Typography variant="body2" fontWeight={600} mb={2} lineHeight={1}>
            {question.text}
            {question.isRequired && (
              <Typography
                component="span"
                color="error.main"
                ml={0.5}
                lineHeight={1}
                fontSize={12}
              >
                *
              </Typography>
            )}
          </Typography>

          {question.type === ModuleQuestionType.Text && (
            <TextField
              fullWidth
              multiline
              rows={4}
              size="small"
              value={getAnswer(question.moduleQuestionID).textAnswer ?? ""}
              onChange={(e) =>
                updateAnswer(question.moduleQuestionID, {
                  textAnswer: e.target.value,
                })
              }
            />
          )}

          {isSingleChoiceType(question.type) && renderSingleChoice(question)}
          {isMultiChoiceType(question.type) && renderMultiChoice(question)}

          {errors[question.moduleQuestionID] && (
            <FormHelperText error>
              {errors[question.moduleQuestionID]}
            </FormHelperText>
          )}
        </Box>
      ))}
    </Stack>
  );
});
